import { makeStyles } from '@material-ui/styles'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import clsx from 'clsx'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import DocumentMeta from 'core/components/DocumentMeta'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import ListContainer from 'core/containers/ListContainer'
import Badges from 'core/elements/badge/Badges'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import Text from 'core/elements/Text'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import Theme from 'core/themes/model'
import UsageProgressBar from 'k8s/components/capacity-and-health/UsageProgressBar'
import DataKeys from 'k8s/DataKeys'
import { humanReadableSize } from 'openstack/helpers'
import { pick, prop } from 'ramda'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { isNilOrEmpty } from 'utils/fp'
import { listHostNetworkConfigs, listResmgrHosts } from '../actions'
import { listClusterHostGroups } from '../clusters/actions'
import { clusterHostGroupsSelector } from '../clusters/selectors'
import { GPU_NOT_CONFIGURED, resmgrHostsSelector } from '../selectors'
import ConfigureGpuHostModal from './ConfigureGpuHostModal'

type ModelDataKey = DataKeys.ResmgrHosts
type SelectorModel = ArrayElement<ReturnType<typeof resmgrHostsSelector>>
type ActionParams = InferActionParams<typeof listResmgrHosts>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {}

const defaultParams: Params = {
  orderBy: 'name',
  orderDirection: 'asc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>('GpuHosts', listTablePrefs)

const searchTargets = ['name', 'id']

const modeString = (str) => {
  return {
    passthrough: 'Passthrough',
    vgpu: 'vGPU',
  }[str]
}

export default function GpuHostsListPage() {
  const classes = useStyles({})
  const { history } = useReactRouter()

  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const [selectedHosts, setSelectedHosts] = useState([])
  const [showConfigureGpuHostModal, setShowConfigureGpuHostModal] = useState(false)

  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)

  const { reload: reloadHostNetworkConfigs } = useListAction(listHostNetworkConfigs)
  const { message, loading, reload, refreshing: refreshingHosts } = useListAction(listResmgrHosts, {
    params,
  })

  const hostData = useSelector(resmgrHostsSelector)
  const filteredHosts = useMemo(() => {
    // filter out non GPU hosts here
    return hostData.filter(
      (host) =>
        host.compatibleModes?.includes('vgpu') || host.compatibleModes?.includes('passthrough'),
    )
  }, [hostData])

  const { loading: loadingClusters } = useListAction(listClusterHostGroups)
  const clusters = useSelector(clusterHostGroupsSelector)

  const batchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      cond: (hosts) => {
        return hosts.length > 0 && hosts.every((host) => host.compatibleModes.includes('vgpu'))
      },
      label: 'Configure',
      icon: 'pen-to-square',
      handleAction: (selected) => {
        setSelectedHosts(selected)
        setShowConfigureGpuHostModal(true)
      },
      refreshAfterSuccess: false,
      disabledTooltip: 'Configure option is only available for hosts that support vGPU',
    },
  ]

  const columns: GridViewColumn<SelectorModel>[] = [
    {
      key: 'name',
      label: 'Name',
      width: 'medium',
    },
    {
      key: 'id',
      label: 'ID',
      display: false,
    },
    {
      key: 'gpuModels',
      label: 'GPU Model',
      render: (val) => val?.join(', '),
    },
    {
      key: 'deviceIds',
      label: 'Device ID',
      render: (val) => val?.join(', '),
    },
    {
      key: 'compatibleModes',
      label: 'Compatible',
      render: (val) => val?.map((mode) => modeString(mode)).join(', '),
    },
    {
      key: 'extensions.resource_usage.data.cumulative_gpu_utilization',
      label: 'Usage',
      CellComponent: ({ value, item }) => {
        if (isNilOrEmpty(value)) {
          return <div>N/A</div>
        }
        const percent = ((value?.used_memory / value?.total_memory) * 100)?.toFixed(0) || 0
        const usedStr = humanReadableSize(value?.used_memory * 1024 * 1024)
        const totalStr = humanReadableSize(value?.total_memory * 1024 * 1024)
        return (
          <div>
            <Text variant="body2">
              <b>{percent}%</b> ({usedStr} / {totalStr})
            </Text>
            <UsageProgressBar percent={percent} />
          </div>
        )
      },
    },
    {
      key: 'gpuMode',
      label: 'GPU Mode',
      CellComponent: ({ value }) => {
        const active = value && value !== GPU_NOT_CONFIGURED
        return (
          <div>
            <FontAwesomeIcon
              className={clsx(classes.icon, active ? classes.activeDot : classes.inactiveDot)}
              solid
            >
              circle
            </FontAwesomeIcon>
            {value}
          </div>
        )
      },
    },
    {
      key: 'allGpuProfiles',
      label: 'Profiles',
      CellComponent: ({ value }) => {
        const profiles = value?.map((profile) => {
          const inGiB = (Number(profile['Memory'].replace('M', '')) / 1024).toFixed(1)
          return {
            text: profile.name,
            tooltipText: `${profile['Max Instances']} slices (${inGiB} GiB Each)`,
          }
        })
        return (
          <div>
            <Text variant="body2" style={{ marginBottom: '8px' }}>
              Available:
            </Text>
            <Badges values={profiles} entityType="labels" showMoreButton />
          </div>
        )
      },
    },
  ]

  return (
    <>
      <DocumentMeta title="GPU Hosts" />
      {showConfigureGpuHostModal && (
        <ConfigureGpuHostModal
          onClose={() => setShowConfigureGpuHostModal(false)}
          rows={selectedHosts}
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        label="GPU Hosts"
        dataKey={DataKeys.ResmgrHosts}
        searchTargets={searchTargets}
        // searchFunctions={searchFunctions}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={() => {
          reload(true)
          reloadHostNetworkConfigs(true)
        }}
        data={filteredHosts}
        columns={columns}
        getParamsUpdater={getParamsUpdater}
        batchActions={batchActions}
        showBreadcrumbs
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  circle: {
    display: 'inline-grid',
    alignItems: 'center',
    gridTemplateColumns: 'minmax(22px, max-content) 1fr',
    whiteSpace: 'nowrap',
    '&:before': {
      content: '""',
      display: 'inherit',
      height: 12,
      width: 12,
      borderRadius: '50%',
      backgroundColor: theme.palette.red.main,
    },
  },
  hostStatusCell: {
    display: 'grid',
    gap: 8,
  },
  missingPackages: {
    color: theme.palette.yellow.main,
    marginRight: 6,
  },
  warning: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    fontSize: 12,
    marginRight: 4,
  },
  activeDot: {
    color: theme.palette.green['700-opaque'],
  },
  inactiveDot: {
    color: theme.palette.grey[200],
  },
}))
